import React, { ReactNode } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { SendApplicationFormValues, SendCreditApplicationRenderProps } from '../types';
import { GET_LENDER_PROFILES_WITH_OCA_ACCESS, QUERY_PORTAL_CONFIGURATION } from 'queries/common';
import { Account, LenderProfilesWithOcaAccessResponse, LenderProfilesWithOcaAccessVariables } from 'types';
import { SendOcaLinkMutationResponse, SendOcaLinkVariables } from './types';
import { formatFormValuesToSendOcaLinkPayload, getLenderProfilesWithOcaOptions } from '../lib';
import { useSelector } from 'react-redux';
import { sendCreditApplicationApi } from './';
import { PortalConfigurationType } from '@trnsact/trnsact-shared-types';

interface Props {
  children: (sendCreditApplicationRenderProps: SendCreditApplicationRenderProps) => ReactNode;
}

export const ApiContainer = ({ children }: Props) => {
  const account = useSelector((state: any) => state.account) as Account;

  const { data, loading: isLenderProfilesWithOcaAccessLoading } = useQuery<
    LenderProfilesWithOcaAccessResponse,
    LenderProfilesWithOcaAccessVariables
  >(GET_LENDER_PROFILES_WITH_OCA_ACCESS, {
    skip: !account?.id,
    variables: { accountId: account.id },
  });

  const { data: portalConfigurationsData, loading: isPortalConfigurationsLoading } = useQuery(
    QUERY_PORTAL_CONFIGURATION,
    {
      skip: !account?.vendorProfileId,
      fetchPolicy: 'no-cache',
      variables: { vendorProfileId: account?.vendorProfileId, types: [PortalConfigurationType.SendApplication] },
    }
  );

  const [sendOcaLink, { loading: isSendOcaLinkLoading }] = useMutation<
    SendOcaLinkMutationResponse,
    SendOcaLinkVariables
  >(sendCreditApplicationApi.sendOcaLink);

  const handleSendOcaLink = async (formValues: SendApplicationFormValues) => {
    const input = formatFormValuesToSendOcaLinkPayload(formValues);
    return (await sendOcaLink({ variables: { input } })) as Promise<SendOcaLinkMutationResponse>;
  };

  return (
    <>
      {children({
        lenderProfilesWithOcaOptions: getLenderProfilesWithOcaOptions(
          data?.lenderProfilesWithOcaAccess?.data ?? [],
          portalConfigurationsData?.portalConfigurations?.[0]?.jsonDefinition?.config
        ),
        handleSendOcaLink,
        isSendOcaLinkLoading,
        isDataLoading: isLenderProfilesWithOcaAccessLoading || isPortalConfigurationsLoading || !account?.id,
      })}
    </>
  );
};
